import type { ComponentProps } from 'react';
import React from 'react';
import styled from '@emotion/styled';
import type { WithRequired } from 'corgi-types';
import type { BorderRadiusProps, ColorProps, PositionProps, SizeProps } from 'styled-system';
import { borderRadius, color, position, size, space, width } from 'styled-system';

import { useIsImageLoaded } from '../../../hooks/image';
import type { CursorProps, ThemeTopProps } from '../../../theme/system-utilities';
import { themeTop } from '../../../theme/system-utilities';
import type { ArticleImage } from '../../../types/Article';
import DefaultSquareProductImage from '../../DefaultProductImage/DefaultSquareProductImage';
import ResponsiveImage from '../../ResponsiveImage';
import { HIGH_PRIORITY_IMAGE_COUNT } from '../constants/constants';

interface ProductImageProps {
  images: ArticleImage[];
  alt?: string;
  /** List of intrinsic responsive image widths in pixels for each of the breakpoints */
  widths: number | number[];
  fallbackSize: number;
  imgIndex?: number;
  isReplaced: boolean;
  visibleProductsCount: number;
}

interface ImageProps extends Omit<ProductImageProps, 'images'> {
  src: string;
}

const UnstyledContainer = styled.div<any>`
  ${position};
  ${space};
  ${width};
`;

const Container = (props: ComponentProps<typeof UnstyledContainer>) => (
  <UnstyledContainer position="relative" pt="100%" width={1} {...props} />
);

const ProductImage = ({
  alt,
  imgIndex = 0,
  isReplaced,
  src,
  widths,
  fallbackSize,
  visibleProductsCount,
}: ImageProps) => {
  const isBelowTheFold = imgIndex >= visibleProductsCount;
  const { ref, isLoaded } = useIsImageLoaded(isBelowTheFold);

  return (
    <Container ref={ref}>
      {isLoaded && (
        <StyledImage
          alt={alt}
          src={src}
          widths={widths}
          fallbackSize={fallbackSize}
          isLazy={isBelowTheFold && !isReplaced}
          highPriority={!isBelowTheFold && imgIndex < HIGH_PRIORITY_IMAGE_COUNT}
        />
      )}
    </Container>
  );
};

const UnstyledStyledImage = styled(ResponsiveImage)<
  ComponentProps<typeof ResponsiveImage> &
    SizeProps &
    CursorProps &
    BorderRadiusProps &
    PositionProps &
    ThemeTopProps &
    ColorProps
>`
  ${color};
  ${borderRadius};
  ${position};
  ${themeTop};
  ${size};
`;

const StyledImage = (props: ComponentProps<typeof UnstyledStyledImage>) => (
  <UnstyledStyledImage
    borderRadius="medium"
    size="100%"
    position="absolute"
    themeTop="sp_0"
    // aria-hidden="true" hides from screen readers to prevent duplicate announcements
    // The product name is already announced via the product card link's accessible name
    // alt text is preserved for SEO and visual context
    aria-hidden="true"
    {...props}
  />
);

const ResponsiveProductImage = ({
  images,
  alt,
  ...props
}: WithRequired<ProductImageProps, 'alt'>) => {
  const src = images[0]?.path;

  if (!src) {
    return <DefaultSquareProductImage title={alt} />;
  }

  return <ProductImage alt={alt} src={src} {...props} />;
};

export default ResponsiveProductImage;
